// Labels pinned over the interior (design system FloatTag): each door's rate with the icon of what
// passes through it («40/min», PRD section 6), each machine's state at a glance, and the bobbing gold
// marker over the machine whose card is open. HTML for crisp text, repositioned only when the camera
// or the content moves.
import { Matrix4, Vector3, type PerspectiveCamera } from 'three';
import type { PieceStatus } from '../sim/report';
import { h, icon } from './dom';
import { percent, perMinute } from './figures';
import { besideDoor } from './label-layout';
import { ICONS } from './icons';
import { itemIcon } from './item-icon';
import { squircle } from './controls';

interface Pinned {
  readonly anchor: Vector3;
  readonly el: HTMLElement;
}

interface Door extends Pinned {
  /** A point further out of the tile: the figure sits on that side of the door. */
  readonly out: Vector3;
  item: string;
  text: string;
  /** The label's size on screen, measured once its text is set (0 until then). */
  width: number;
  height: number;
}

const STATE_TEXT: Readonly<Record<PieceStatus, string>> = {
  working: 'Al máximo',
  bottleneck: 'Al máximo',
  starved: 'Falta material',
  blocked: 'Salida llena',
  disconnected: 'Sin salida',
};

export class InteriorLabels {
  readonly el: HTMLElement;
  private readonly doors = new Map<string, Door>();
  private readonly machines = new Map<string, Pinned & { key: string }>();
  private readonly marker: Pinned;
  private markerOn = false;
  private readonly last = new Matrix4();
  private readonly viewProjection = new Matrix4();
  private readonly projected = new Vector3();
  private readonly projectedOut = new Vector3();
  private lastWidth = 0;
  private lastHeight = 0;
  private dirty = true;

  constructor() {
    this.el = h('div', { class: 'labels interior-labels', hidden: true });
    this.marker = { anchor: new Vector3(), el: h('div', { class: 'select-marker', 'aria-hidden': 'true' }, squircle(icon(ICONS.caretDown, 'icon select-marker__icon'), { size: 44, tone: 'gold' })) };
    this.marker.el.hidden = true;
    this.el.append(this.marker.el);
  }

  /**
   * A door's figure: what passes through it per minute, with the icon of the item. It floats beside
   * `anchor` (the top of the gate), on the side of `out`, outside the tile.
   */
  setDoor(id: string, anchor: { readonly at: Vector3; readonly out: Vector3 }, kind: 'in' | 'out', rate: number, item: string | undefined): void {
    let door = this.doors.get(id);
    if (!door) {
      const el = h('div', { class: `tag tag--rate tag--door tag--door-${kind}`, 'data-door': id });
      door = { anchor: anchor.at, out: anchor.out, el, item: '', text: '', width: 0, height: 0 };
      this.doors.set(id, door);
      this.el.append(el);
    }
    const text = perMinute(rate);
    const shownItem = item ?? '';
    if (door.text !== text || door.item !== shownItem) {
      door.text = text;
      door.item = shownItem;
      door.width = 0;
      door.el.replaceChildren(...(item ? [itemIcon(item, 26)] : []), h('span', {}, text));
      door.el.setAttribute('aria-label', `${kind === 'in' ? 'Entrada' : 'Salida'}: ${text}`);
      this.dirty = true;
    }
  }

  /** Where the doors' figures are on screen, relative to the labels' layer. */
  doorRects(): { left: number; top: number; right: number; bottom: number }[] {
    const origin = this.el.getBoundingClientRect();
    return [...this.doors.values()].map(({ el }) => {
      const r = el.getBoundingClientRect();
      return { left: r.left - origin.left, top: r.top - origin.top, right: r.right - origin.left, bottom: r.bottom - origin.top };
    });
  }

  /** Each machine's state chip; machines that are gone lose theirs. */
  setMachines(list: readonly { readonly id: string; readonly anchor: Vector3; readonly status: PieceStatus; readonly work: number }[]): void {
    const seen = new Set<string>();
    for (const m of list) {
      seen.add(m.id);
      let pinned = this.machines.get(m.id);
      if (!pinned) {
        pinned = { anchor: m.anchor.clone(), el: h('div', { class: 'state-chip', 'data-machine': m.id }), key: '' };
        this.machines.set(m.id, pinned);
        this.el.append(pinned.el);
        this.dirty = true;
      }
      if (!pinned.anchor.equals(m.anchor)) {
        pinned.anchor.copy(m.anchor);
        this.dirty = true;
      }
      const tone = m.status === 'working' || m.status === 'bottleneck' ? 'ok' : m.status === 'disconnected' ? 'idle' : 'warn';
      const text = tone === 'ok' ? percent(m.work) : tone === 'warn' ? `${STATE_TEXT[m.status]} · ${percent(m.work)}` : STATE_TEXT[m.status];
      const key = `${tone}:${text}`;
      if (key !== pinned.key) {
        pinned.key = key;
        pinned.el.className = `state-chip state-chip--${tone}`;
        const glyph = tone === 'ok' ? ICONS.checkCircle : tone === 'warn' ? ICONS.hourglass : ICONS.linkBreak;
        pinned.el.replaceChildren(icon(glyph, 'icon state-chip__icon'), h('span', {}, text));
        pinned.el.setAttribute('aria-label', `${STATE_TEXT[m.status]}, ${percent(m.work)}`);
      }
    }
    for (const [id, pinned] of this.machines) {
      if (seen.has(id)) continue;
      pinned.el.remove();
      this.machines.delete(id);
    }
  }

  /** The gold marker over the selected machine, or none. */
  setMarker(anchor: Vector3 | undefined): void {
    this.markerOn = !!anchor;
    if (anchor && !anchor.equals(this.marker.anchor)) this.marker.anchor.copy(anchor);
    this.marker.el.hidden = !anchor;
    this.dirty = true;
  }

  /** Repositions the labels if the camera, the viewport or the labels changed. */
  update(camera: PerspectiveCamera, width: number, height: number): void {
    this.viewProjection.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    if (!this.dirty && this.viewProjection.equals(this.last) && width === this.lastWidth && height === this.lastHeight) return;
    this.dirty = false;
    this.last.copy(this.viewProjection);
    this.lastWidth = width;
    this.lastHeight = height;
    const place = (p: Pinned): void => {
      const v = this.projected.copy(p.anchor).applyMatrix4(this.viewProjection);
      const visible = v.z < 1 && Math.abs(v.x) <= 1.1 && Math.abs(v.y) <= 1.1;
      p.el.style.visibility = visible ? '' : 'hidden';
      if (!visible) return;
      const x = ((v.x + 1) / 2) * width;
      const y = ((1 - v.y) / 2) * height;
      p.el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) translate(-50%, -100%)`;
    };
    for (const d of this.doors.values()) {
      if (d.width === 0) {
        // Measured while laid out; a hidden layer gives 0, and it is measured again next time.
        d.width = d.el.offsetWidth;
        d.height = d.el.offsetHeight;
        if (d.width === 0) this.dirty = true;
      }
      const a = this.projected.copy(d.anchor).applyMatrix4(this.viewProjection);
      const o = this.projectedOut.copy(d.out).applyMatrix4(this.viewProjection);
      const visible = a.z < 1 && Math.abs(a.x) <= 1.1 && Math.abs(a.y) <= 1.1;
      d.el.style.visibility = visible ? '' : 'hidden';
      if (!visible) continue;
      const toScreen = (v: Vector3): { x: number; y: number } => ({ x: ((v.x + 1) / 2) * width, y: ((1 - v.y) / 2) * height });
      const { left, top } = besideDoor(toScreen(a), toScreen(o), d.width, d.height);
      d.el.style.transform = `translate3d(${left.toFixed(1)}px, ${top.toFixed(1)}px, 0)`;
    }
    for (const m of this.machines.values()) place(m);
    if (this.markerOn) place(this.marker);
  }

  clear(): void {
    for (const d of this.doors.values()) d.el.remove();
    for (const m of this.machines.values()) m.el.remove();
    this.doors.clear();
    this.machines.clear();
    this.setMarker(undefined);
  }
}
